import { motion } from "framer-motion";
import { Cloud, ShieldCheck, Radar, Brain, ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    code: "01",
    icon: Cloud,
    title: "Enterprise Cloud Security Specialist",
    lead:
      "Architecting and hardening cloud security postures for regulated enterprises — landing zones, identity, data and workload protection across Azure, AWS and Google Cloud.",
    chips: ["Azure", "AWS", "GCP", "Zero Trust"],
    accent: "blue",
  },
  {
    code: "02",
    icon: ShieldCheck,
    title: "Microsoft Security Consultant",
    lead:
      "Deep specialism in the Microsoft Security ecosystem — Defender XDR, Sentinel, Entra ID, Intune, Purview — designing and operationalising end-to-end defence.",
    chips: ["Defender XDR", "Sentinel", "Entra ID", "Purview"],
    accent: "cyan",
  },
  {
    code: "03",
    icon: Radar,
    title: "SOC Transformation Advisor",
    lead:
      "Modernising security operations centres — detection engineering, SIEM/SOAR pipelines, threat hunting playbooks and SOC maturity benchmarking for global teams.",
    chips: ["SIEM/SOAR", "Detection Eng.", "KQL", "Hunt Ops"],
    accent: "indigo",
  },
  {
    code: "04",
    icon: Brain,
    title: "AI Security & Threat Hunting Specialist",
    lead:
      "Securing the next decade of AI workloads — GenAI threat modelling, LLM red-teaming, prompt-injection defence, and hands-on attack simulation against real adversary TTPs.",
    chips: ["GenAI Defence", "LLM Red-Team", "ATT&CK", "Copilot Sec"],
    accent: "cyan",
  },
];

const accentMap = {
  blue: { dot: "#60A5FA", border: "rgba(96,165,250,0.30)", soft: "rgba(96,165,250,0.10)" },
  cyan: { dot: "#22D3EE", border: "rgba(34,211,238,0.30)", soft: "rgba(34,211,238,0.10)" },
  indigo: { dot: "#818CF8", border: "rgba(129,140,248,0.30)", soft: "rgba(129,140,248,0.10)" },
};

const CAPABILITIES = [
  "Global remote delivery",
  "Hands-on attack simulation",
  "Microsoft Security ecosystem",
  "24×7 SOC engineering",
  "Hybrid & multi-cloud",
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle cyber backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 380px at 90% 10%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(600px 340px at 5% 90%, rgba(6,182,212,0.07), transparent 65%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,165,250,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-blue-500" />
          <span className="label-eyebrow">00 — Profile</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-14 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] tracking-tighter leading-[1.04] text-white">
            Enterprise security, <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#67E8F9 0%,#60A5FA 50%,#A5B4FC 100%)",
              }}
            >
              engineered for the boardroom and the SOC.
            </span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed text-base md:text-lg">
            I work with Fortune-500 security teams to modernise cloud defence,
            mature SOC operations, and operationalise AI security — strategy
            you can defend in a board meeting, controls you can ship on Monday.
          </p>
        </div>

        {/* Pillar grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {PILLARS.map((p, i) => {
            const a = accentMap[p.accent];
            const Icon = p.icon;
            return (
              <motion.article
                key={p.code}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`about-pillar-${p.code}`}
                className="group relative rounded-2xl p-7 md:p-8 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: "linear-gradient(180deg,rgba(10,12,19,0.85) 0%,rgba(7,10,18,0.95) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
                }}
              >
                {/* Glow accent on hover */}
                <div
                  aria-hidden
                  className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: `radial-gradient(400px 200px at 80% 0%, ${a.soft}, transparent 70%)`,
                  }}
                />
                {/* Top row */}
                <div className="relative flex items-start justify-between">
                  <div
                    className="h-11 w-11 rounded-lg grid place-items-center"
                    style={{
                      background: a.soft,
                      border: `1px solid ${a.border}`,
                      color: a.dot,
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
                    {p.code}
                  </span>
                </div>

                <h3 className="relative mt-6 font-display font-semibold text-xl md:text-[1.45rem] tracking-tight text-white leading-snug">
                  {p.title}
                </h3>
                <p className="relative mt-3 text-sm md:text-[0.95rem] text-slate-400 leading-relaxed">
                  {p.lead}
                </p>

                <div className="relative mt-5 flex flex-wrap gap-1.5">
                  {p.chips.map((c) => (
                    <span
                      key={c}
                      className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border text-slate-300"
                      style={{ borderColor: a.border, background: a.soft }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Capability strip */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 md:mt-12 rounded-2xl border border-white/10 px-6 md:px-8 py-5 flex flex-wrap items-center gap-x-6 gap-y-3"
          style={{ background: "rgba(10,12,19,0.6)", backdropFilter: "blur(8px)" }}
          data-testid="about-capability-strip"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Delivery model ▾
          </span>
          {CAPABILITIES.map((c, i) => (
            <span key={c} className="inline-flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              <span className="text-sm text-slate-200">{c}</span>
              {i < CAPABILITIES.length - 1 && (
                <span className="text-slate-700 ml-3 hidden md:inline">·</span>
              )}
            </span>
          ))}
          <a
            href="#contact"
            data-testid="about-engage-cta"
            className="ml-auto inline-flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors"
          >
            Engage on a scoping call <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
